import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectCPMFTrackingForecastLocationId,
  selectCPMFTrackingForecastMultiLocationIds,
  selectCPMFTrackingForecastTimeFrequency,
  selectCPMFTrackingForecastModelId,
  selectCPMFTrackingForecastMultiModelIds,
  selectCPMFTrackingForecastForecastDate,
  selectCPMFTrackingForecastVariable,
  selectCPMFTrackingForecastSubpage,
  selectCPMFTrackingForecastRounds,
  selectCPMFTrackingForecastMembers,
  selectCPMFTrackingForecastChartType,
} from 'redux/reducers/meteorologyFieldsTrackingForecastControlPanelReducer';
import {
  GetChartDataParams,
} from 'types/Meteorology/Fields/TrackingForecast/DetailedView/getChartData';
import type { MeteorologyFieldsTrackingForecastControlPanelState as T } from 'redux/reducers/meteorologyFieldsTrackingForecastControlPanelReducer';

export default function useChartDataParams() {
  const subpage: T['subpage'] = useSelector(selectCPMFTrackingForecastSubpage);
  const chartType: T['chartType'] = useSelector(selectCPMFTrackingForecastChartType);
  const locationId: T['locationId'] = useSelector(selectCPMFTrackingForecastLocationId);
  const multiLocationIds: T['multilocationIds'] = useSelector(selectCPMFTrackingForecastMultiLocationIds);
  const timeFrequency: T['timeFrequency'] = useSelector(selectCPMFTrackingForecastTimeFrequency);
  const modelId: T['modelId'] = useSelector(selectCPMFTrackingForecastModelId);
  const multiModelIds: T['multimodelIds'] = useSelector(selectCPMFTrackingForecastMultiModelIds);
  const multiMembers: T['members'] = useSelector(selectCPMFTrackingForecastMembers);
  const forecastDate: T['forecastDate'] = useSelector(selectCPMFTrackingForecastForecastDate);
  const rounds: T['rounds'] = useSelector(selectCPMFTrackingForecastRounds);
  const variable: T['variable'] = useSelector(selectCPMFTrackingForecastVariable);

  const multiLocationParams: GetChartDataParams | undefined = useMemo(() => {
    if (subpage !== 'locations') return undefined;

    const paramsAreValid = (
      multiLocationIds.length > 0
      && timeFrequency !== undefined
      && modelId !== undefined
      && variable !== undefined
      && forecastDate.length > 0
    );

    if (!paramsAreValid) return undefined;

    return {
      subpage,
      chartType,
      locationIds: multiLocationIds,
      timeFrequency,
      modelIds: [modelId],
      variable,
      forecastDates: [forecastDate],
    };
  }, [
    subpage, chartType, multiLocationIds, timeFrequency, modelId, variable, forecastDate,
  ]);

  const multiMemberParams: GetChartDataParams | undefined = useMemo(() => {
    if (subpage !== 'members') return undefined;

    const paramsAreValid = (
      locationId !== undefined
      && timeFrequency !== undefined
      && modelId !== undefined
      && multiMembers.length > 0
      && variable !== undefined
      && forecastDate.length > 0
    );

    if (!paramsAreValid) return undefined;

    return {
      subpage,
      chartType,
      locationId,
      timeFrequency,
      modelIds: [modelId],
      memberIds: multiMembers,
      variable,
      forecastDates: [forecastDate],
    };
  }, [
    subpage, chartType, locationId, timeFrequency, modelId, multiMembers, variable, forecastDate,
  ]);

  const multiModelParams: GetChartDataParams | undefined = useMemo(() => {
    if (subpage !== 'models') return undefined;

    const paramsAreValid = (
      locationId !== undefined
      && timeFrequency !== undefined
      && multiModelIds.length > 0
      && variable !== undefined
      && forecastDate.length > 0
    );

    if (!paramsAreValid) return undefined;

    return {
      subpage,
      chartType,
      locationId,
      timeFrequency,
      modelIds: multiModelIds,
      variable,
      forecastDates: [forecastDate],
    };
  }, [
    subpage, chartType, locationId, timeFrequency, multiModelIds, variable, forecastDate,
  ]);

  const multiRoundParams: GetChartDataParams | undefined = useMemo(() => {
    if (subpage !== 'rounds') return undefined;

    const paramsAreValid = (
      locationId !== undefined
      && timeFrequency !== undefined
      && modelId !== undefined
      && variable !== undefined
      && rounds.length > 0
    );

    if (!paramsAreValid) return undefined;

    return {
      subpage,
      chartType,
      locationId,
      timeFrequency,
      modelIds: [modelId],
      variable,
      forecastDates: rounds,
    };
  }, [
    subpage, chartType, locationId, timeFrequency, modelId, variable, rounds,
  ]);

  const params: GetChartDataParams | undefined = useMemo(() => {
    switch (subpage) {
      case 'locations': return multiLocationParams;
      case 'members': return multiMemberParams;
      case 'models': return multiModelParams;
      case 'rounds': return multiRoundParams;
      default: return undefined;
    }
  }, [subpage, multiLocationParams, multiMemberParams, multiModelParams, multiRoundParams]);

  return params;
}
